import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useQueryAppointableAsPublic,
  useQueryAppointmentIdAsMember,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { FacilityDTO, UserStaffPublicDTO } from "@gymflow/types";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { RouteFeature } from "apps/portal/src/routes";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { SessionOverlayContext } from "../../../providers/authentication/SessionOverlay/SessionOverlayContext";
import useGymflowModels from "../../../store";
import { Button, Spinner, UsersIcon } from "../../atoms";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";

export type HostedPagesAppointmentSelectProps = { mode: "edit" | "create" };

export const HostedPagesAppointmentSelect: React.FC<
  HostedPagesAppointmentSelectProps
> = ({ mode }) => {
  const { userId: id } = useContext(AuthenticationContext);
  const { setSessionOverlayState } = useContext(SessionOverlayContext);
  const { appointableId, appointmentId } = useParams<{
    appointableId?: string;
    appointmentId?: string;
  }>();
  const { createSiteOrEmbedLink, isEmbed } = usePortalRoutes();
  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(RouteFeature.Appointables),
  );
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { data: dataAppointment, isLoading: isLoadingAppointment } =
    useQueryAppointmentIdAsMember({
      api,
      id: Number(appointmentId),
      enabled: mode === "edit",
    });

  const { data: appointable, isLoading: isLoadingAppointable } =
    useQueryAppointableAsPublic({
      api,
      id:
        mode === "create" && appointableId
          ? parseInt(appointableId)
          : dataAppointment?.appointable.id,
    });

  const [parent] = useAutoAnimate();
  const [innerParent] = useAutoAnimate();

  const isLoading = isLoadingAppointment || isLoadingAppointable;

  const nextPageLinkNoPreference = createSiteOrEmbedLink(
    mode === "create"
      ? RouteFeature.AppointableIdSelectTime.replace(
          ":appointableId",
          String(appointableId),
        ).replace(":staffOrFacilityId", "no-preference")
      : RouteFeature.AppointableIdSelectTimeReschedule.replace(
          ":appointmentId",
          String(appointmentId),
        ).replace(":staffOrFacilityId", "no-preference"),
  );

  const nextPageLinkPreference = (e: FacilityDTO | UserStaffPublicDTO) =>
    createSiteOrEmbedLink(
      (mode === "create"
        ? RouteFeature.AppointableIdSelectTime.replace(
            ":appointableId",
            String(appointableId),
          )
        : RouteFeature.AppointableIdSelectTimeReschedule.replace(
            ":appointmentId",
            String(appointmentId),
          )
      ).replace(":staffOrFacilityId", e.id.toString()),
    );

  return (
    <div
      className={cn(
        "flex w-full flex-col overflow-y-auto bg-gray-50 dark:bg-gray-800",
        {
          "h-fit track-height": isEmbed,
          "h-full": !isEmbed,
        },
      )}
    >
      <HostedPagesBackHeader
        label={
          isLoading
            ? ""
            : ["FACILITY", "FACILITY_AND_STAFF"].includes(
                  appointable?.availabilityType || "",
                )
              ? t("pages.hostedPagesAppointableSelect.facility")
              : t("pages.hostedPagesAppointableSelect.host")
        }
      />
      <div ref={parent} className="flex h-full flex-col items-center">
        {isLoading && <Spinner />}
        {!isLoading && appointable && (
          <div
            ref={innerParent}
            className="flex w-[32rem] max-w-full flex-col items-center gap-2 overflow-y-auto overflow-x-hidden p-6 max-lg:justify-between"
          >
            <Button
              {...(id
                ? {
                    link: nextPageLinkNoPreference,
                  }
                : {
                    onClick: async () => {
                      setSessionOverlayState({
                        screen: "try-sign-in",
                      });
                    },
                  })}
              className="shadow-xs flex h-fit w-full flex-row items-center justify-between border-gray-200 px-3 py-2 dark:border-gray-800"
            >
              <div className="flex flex-row items-center gap-4">
                <UsersIcon pathClassName="stroke-gray-500" />
                <div className="flex flex-col items-start">
                  <div className="dark:text-gray-0 text-sm font-semibold text-gray-950">
                    {t("pages.hostedPagesAppointableSelect.noPreference")}
                  </div>
                  <div className="text-sm font-normal text-gray-500">
                    {t(
                      "pages.hostedPagesAppointableSelect.maximumAvailability",
                    )}
                  </div>
                </div>
              </div>
              <FontAwesomeIcon
                icon={faAngleRight}
                className="size-4 text-gray-500"
              />
            </Button>
            <div className="my-2 flex w-full border-b border-gray-300" />

            {(["FACILITY", "FACILITY_AND_STAFF"].includes(
              appointable.availabilityType,
            )
              ? appointable.appointableFacilities
              : appointable.appointableHosts
            ).map((e) => {
              return (
                <Button
                  key={e.id}
                  className="shadow-xs flex h-fit w-full flex-row items-center justify-between border-gray-200 px-3 py-2 dark:border-gray-800"
                  {...(id
                    ? {
                        link: nextPageLinkPreference(e),
                      }
                    : {
                        onClick: async () => {
                          setSessionOverlayState({
                            screen: "try-sign-in",
                          });
                        },
                      })}
                >
                  <div className="flex flex-row items-center gap-4">
                    <div className="flex size-8 items-center justify-center overflow-hidden rounded-full border border-gray-100 dark:border-gray-900">
                      {e.picture ? (
                        <img src={e.picture} alt="avatar" />
                      ) : (
                        <div className="flex size-5 items-center justify-center font-semibold text-gray-500">
                          {"firstName" in e
                            ? e.firstName[0] + e.lastName[0]
                            : e.name[0]}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="dark:text-gray-0 text-sm font-semibold text-gray-950">
                        {"firstName" in e
                          ? `${e.firstName} ${e.lastName}`
                          : e.name}
                      </div>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    className="size-4 text-gray-500"
                  />
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
